import Component from '@glimmer/component';
import { service } from '@ember/service';
import { hash } from '@ember/helper';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import {
  HdsIcon,
  HdsIconTile,
  HdsTextDisplay,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import { Flex, Loading } from 'core/components';
import Card from 'hcp/components/page/projects/dashboard/card';

/**
 * A billing model.
 * @argument billing
 * @type {number}
 */
/**
 *
 * `organization` is the organization context.
 * @argument organization;
 * @type {Object}
 *
 */
/**
 *
 * `project` is the project context.
 * @argument project;
 * @type {Object}
 *
 */
/**
 *
 * `PageProjectsDashboard::BillingSummaryCard` displays the billing card on the Project dashboard.
 *
 *
 * ```
 * <Page::Projects::Dashboard::BillingSummaryCard
 *   @billing={{this.userContext.billing}}
 *   @organization={{@organization}}
 *   @project={{@project}}
 * />
 * ```
 *
 * @class PageProjectsDashboardBillingSummaryCard
 *
 */
export default class PageProjectsDashboardBillingSummaryCard extends Component {
  @service billing;
  @service('resources.project') projects;
  @service userContext;
  @service systemStatus;

  constructor() {
    super(...arguments);

    if (!this.userContext.billing) {
      this.setup.perform();
    }
  }

  get statusPageUrl() {
    return this.systemStatus.statusPageUrl;
  }

  get showLoading() {
    return this.setup.isRunning || !this.userContext.billing;
  }

  setup = task(async () => {
    const { organization, project } = this.args;
    if (organization && project) {
      this.userContext.billing = await this.billing.getBilling(
        organization.id,
        project.id
      );
    }
  });
  <template>
    <Card class="billing-summary-card" {{didInsert this.setup.perform}}>
      <:header>
        <HdsIconTile
          @icon="credit-card"
          data-test-project-dashboard-billing-header-icon
        />
        <HdsTextDisplay
          @size="400"
          @weight="bold"
          @tag="h2"
          data-test-project-dashboard-billing-header
        >
          {{t "service-card.billing.title"}}
        </HdsTextDisplay>
      </:header>
      <:body>
        {{#if this.showLoading}}
          <Loading data-test-loading as |L|>
            <L.Message>
              {{t "service-card.billing.loading"}}
            </L.Message>
          </Loading>
        {{else if @billing.billingAccount}}
          <Flex
            @alignItems="start"
            @direction="row"
            @gap="sm"
            data-test-project-dashboard-billing-content
          >
            <span>
              <HdsIcon
                @name="info"
                @isInline={{true}}
                data-test-project-dashboard-billing-info-icon
              />
            </span>
            <p class="hds-typography-body-200 hds-font-weight-regular">
              {{t "service-card.billing.organization-info"}}
            </p>
          </Flex>
        {{else}}
          {{! TODO: update to display billing unavailable error in follow up }}
          <Loading data-test-loading as |L|>
            <L.Message>
              {{t "service-card.billing.loading"}}
            </L.Message>
          </Loading>
        {{/if}}
      </:body>
      <:footer>
        {{#if (and @billing.billingAccount (not this.showLoading))}}
          <HdsLinkStandalone
            data-test-billingaccount-card-link
            @route="billing.accounts.account"
            @model={{@billing.billingAccount.id}}
            @query={{hash org_id=@billing.billingAccount.organizationId}}
            @text={{t "service-card.billing.view-billing-organization"}}
            @icon="arrow-right"
            @iconPosition="trailing"
            data-test-project-dashboard-billing-link
          />
        {{/if}}
      </:footer>
    </Card>
  </template>
}
